<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2>{{ $t('performance_dashboard') }}</h2>
        <info-modal>
          <template v-slot:button="{ on, attrs }">
            <v-chip class="ml-4" dark v-bind="attrs" v-on="on">
              <v-icon class="mr-1">mdi-information-outline</v-icon>
              {{ $t('beta') }}
            </v-chip>
          </template>
          <v-card-title>
            <h4>{{ $t('performance_dashboard_ui.beta_modal_title') }}</h4>
          </v-card-title>
          <v-card-text v-html="$t('performance_dashboard_ui.beta_message')"/>
        </info-modal>
      </div>
      <customer-multi-select
        id="customer-select"
        ref="customer-select"
        :title="$t('select_business_units')"
        v-model="selectedCustomers">
        <template #activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" color="primary">
            <v-icon>mdi-selection-multiple</v-icon>
            {{ $t('performance_dashboard_ui.select_organizations') }}
          </v-btn>
        </template>
      </customer-multi-select>
    </div>
    <div class="mb-2">
      <div class="text-subtitle-1 mr-2">{{ $t('displaying') }}:</div>
      <v-chip-group column dark active-class="active-chip">
        <v-chip
          v-for="chip in displayedCustomerChips"
          :key="chip.value"
          :to="chip.to">
          <v-icon v-if="chip.icon">{{ chip.icon }}</v-icon>
          {{ chip.text }}
        </v-chip>
      </v-chip-group>
    </div>
    <v-divider class="mb-4"/>
    <v-item-group mandatory v-model="activeTab">
      <v-row>
        <v-col v-for="tab in tabs" :key="tab.name" cols="12" sm="6" md="3" lg="3">
          <v-item v-slot="{ active, toggle }" v-model="tab.name">
            <v-card @click="toggle" :class="active ? 'blue-grey lighten-5' : ''">
              <v-card-title class="text-h5 text-truncate mr-2">{{ tab.label }}</v-card-title>
              <v-card-text class="d-flex align-center mb-2">
                <v-avatar size="60" color="grey darken-2">
                  <div class="status-avatar px-3">
                    <v-progress-circular v-if="tab.loading" indeterminate color="white" />
                    <span v-else>{{ tab.value }}%</span>
                  </div>
                </v-avatar>
                <div class="text-subtitle-1 ml-3">{{ tab.valueLabel }}</div>
              </v-card-text>
              <v-card-actions class="d-flex justify-center">
                <v-icon class="chevron-down" :class="{ 'active': active }">mdi-chevron-double-down</v-icon>
              </v-card-actions>
            </v-card>
          </v-item>
        </v-col>
      </v-row>
      <div v-for="tab in tabs" :key="tab.name">
        <component v-bind:is="tab.component"
           v-show="activeTab === tab.name"
           :active="activeTab === tab.name"
           :selected-customers="selectedCustomers"
           v-on:loading="tab.loading = true"
           v-on:loaded="tabLoaded(tab, $event)"/>
      </div>
    </v-item-group>
  </v-container>
</template>

<script>
import InfoModal from '@/components/shared/InfoModal'
import CustomerMultiSelect from '@/components/performance-dashboard/CustomerMultiSelect'
import FleetHealthDashboard from '@/components/performance-dashboard/FleetHealthDashboard'
import TemperatureViolationsDashboard from '@/components/performance-dashboard/TemperatureViolationsDashboard'
import RoutesDashboard from '@/components/performance-dashboard/RoutesDashboard'
import RootCauseDashboard from '@/components/performance-dashboard/RootCauseDashboard'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import { filter, intersectionBy, map, round, isEmpty, take, size } from 'lodash'

export default {
  name: 'PerformanceDashboard',
  components: {
    InfoModal,
    CustomerMultiSelect,
    FleetHealthDashboard,
    TemperatureViolationsDashboard,
    RoutesDashboard,
    RootCauseDashboard
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      tabIndex: 0,
      numDisplayedCustomers: 20,
      activeTab: 'fleet-health',
      tabs: [
        {
          name: 'fleet-health',
          loading: true,
          label: this.$t('fleet_health'),
          valueLabel: this.$t('performance_dashboard_ui.overall_fleet_health'),
          value: null,
          component: FleetHealthDashboard
        },
        {
          name: 'violations',
          loading: true,
          label: this.$tc('temperature_violation', 2),
          valueLabel: this.$t('performance_dashboard_ui.in_spec_routes'),
          value: null,
          component: TemperatureViolationsDashboard
        },
        {
          name: 'route-plans',
          loading: true,
          label: this.$tc('route', 2),
          valueLabel: this.$t('linked_plans'),
          value: null,
          component: RoutesDashboard
        },
        {
          name: 'route-assessments',
          loading: true,
          label: this.$tc('root_cause', 2),
          valueLabel: this.$t('performance_dashboard_ui.root_causes_completed'),
          value: null,
          component: RootCauseDashboard
        }
      ]
    }
  },
  methods: {
    /**
     * Handle a loaded event from a dashboard subcomponent.
     * @param tab
     * @param event
     */
    tabLoaded (tab, event) {
      tab.loading = false
      tab.value = round(event.value)
    }
  },
  computed: {
    defaultCustomerSelection () {
      // If there's any top level customers, default to those (user is probably an admin)
      const topLevelCustomers = filter(this.customers, x => x.isTopLevel())
      return topLevelCustomers.length > 0 ? topLevelCustomers : this.customers
    },
    /**
     * Computed property to back the currently selected customers with the route customerIds param. This way, every time
     * the selected customers changes, the url will change, and vice versa. This will let users use the back/forward
     * buttons to jump through various searches to compare. It also lets the user back out of viewing a single customer.
     */
    selectedCustomers: {
      get () {
        const param = this.$route.params.customerIds
        // If no customers specified, default to a particular set of customers depending. We may simplify this at some
        // point and just default to all the user's customers
        if (param == null) {
          return this.defaultCustomerSelection
        }
        const split = param.split(',')
        return filter(this.customers, x => split.includes(x.id.toString()))
      },
      set (newValue) {
        const customerParameters = map(newValue, x => x.id).join(',')
        // If nothing has changed, don't do anything.
        if (customerParameters === this.$route.params.customerIds) {
          return
        }
        if (this.customers.length > 1) {
          // Check to see if default customers selected. If so, we can revert to default route.
          const intersectingIds = intersectionBy(newValue, this.defaultCustomerSelection, x => x.id)
          if (intersectingIds.length === this.defaultCustomerSelection.length) {
            this.$router.push({ path: '/performance/dashboard' })
          } else {
            // Otherwise, go to route appropriate for the customer selection.
            this.$router.push({ path: `/performance/dashboard/customers/${customerParameters}` })
          }
        }
      }
    },
    displayedCustomerChips () {
      const result = map(take(this.selectedCustomers, this.numDisplayedCustomers), customer => {
        return {
          value: customer.id,
          text: customer.name,
          icon: customer.isTopLevel() ? 'mdi-office-building' : 'mdi-garage',
          color: 'grey darken-2',
          to: `/performance/dashboard/customers/${customer.id}`
        }
      })
      const numOver = size(this.selectedCustomers) - this.numDisplayedCustomers
      if (numOver > 0) {
        result.push({
          value: '-1',
          text: this.$t('and_n_more', { n: numOver }),
          color: 'black'
        })
      }
      return result
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$t('performance_dashboard'))
    // If selectedCustomers is empty, then the user somehow ended up on a URL that yields no users for them, and we
    // can't render the page correctly. Easiest way to handle this situation is to just redirect to the 404 page.
    if (isEmpty(this.selectedCustomers)) {
      this.$router.push({ path: '/404' })
    }
  }
}
</script>

<style scoped lang="scss">
.active-chip {
  background-color: black;
  color: white;
}
.status-avatar {
  color: white;
  font-size: 20px;
}
.chevron-down {
  visibility: hidden;
}
.chevron-down.active {
  visibility: inherit;
}

</style>
